/**
 * ExportBackgroundTaskRunningModal — informational dialog shown when an export
 * task is currently being processed in the background.
 *
 * When triggered, this modal informs the user that their export task is still
 * running, displays the associated taskId, and prevents further export actions
 * until the task completes.
 *
 * Features:
 *   - Shows the current taskId for reference
 *   - "Go to Activity Monitor" button navigates to the tasks page
 *   - "Close" button dismisses the modal
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import GenericModal from '../GenericModal'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ROUTES from '../../consts/paths'
import { useUserRole } from '../../hooks/useUserRole'

interface TaskRunningModalProps {
  isOpen: boolean
  onClose: () => void
  taskId: string
}

export const ExportBackgroundTaskRunningModal: React.FC<TaskRunningModalProps> = ({ isOpen, onClose, taskId }) => {
  const navigate = useNavigate()
  const { isGlobalRole } = useUserRole()

  return (
    <GenericModal
      title="Export Task Processing"
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="sm"
      confirmActionText="Go to Activity Monitor"
      onConfirm={async () => { navigate(ROUTES.TASKS) }}
      hideConfirmButton={!isGlobalRole}
      destructiveButtonText="Close"
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap={3} py={2}>
        <InfoOutlinedIcon sx={{ fontSize: 64, color: 'primary.main' }} />
        <Box textAlign="center">
          <Typography variant="body1" gutterBottom>
            Your export task is currently being processed in the background.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>Task ID:</strong> {taskId}
          </Typography>
          {isGlobalRole
            ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                You can monitor the progress of this task in the Activity Monitor.
                Once the task is complete, you will be able to export more data.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Please check back later or refresh the Activity Monitor to see when the task has finished.
              </Typography>
            </>
              )
            : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              You will be notified once the task is complete and you will be able to export more data.
            </Typography>
              )}
        </Box>
      </Box>
    </GenericModal>
  )
}

export default ExportBackgroundTaskRunningModal
